/**
 * ShippingLogistics — compact secondary section: maritime and logistics services,
 * usable standalone or in support of the project work above.
 */

import { memo } from "react";
import { useTranslation } from "react-i18next";
import Reveal from "@/components/common/Reveal";
import shippingImage from "@/assets/port-ship-agency-1.jpg";

interface Item {
  title: string;
  description: string;
}

const ShippingLogistics = () => {
  const { t } = useTranslation();
  const items = t("home.shipping.items", { returnObjects: true }) as Item[];

  return (
    <section
      id="shipping"
      className="bg-muted/60 py-16 md:py-20 scroll-mt-20 border-t border-border"
      aria-labelledby="shipping-heading"
    >
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <Reveal className="lg:col-span-5 group overflow-hidden bg-muted">
            <img
              src={shippingImage}
              alt={t("home.shipping.imageAlt")}
              className="aspect-[5/3] w-full object-cover object-center transition-transform duration-[900ms] ease-out group-hover:scale-[1.04]"
              width="1600"
              height="960"
              loading="lazy"
              decoding="async"
            />
          </Reveal>

          <Reveal delay={90} className="lg:col-span-7">
            <div className="flex items-center gap-4 mb-4">
              <span className="rule w-10 shrink-0" aria-hidden="true" />
              <p className="eyebrow text-cobalt">{t("home.shipping.label")}</p>
            </div>
            <h2 id="shipping-heading" className="display-md text-foreground mb-3">
              {t("home.shipping.heading")}
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-6 max-w-2xl">
              {t("home.shipping.intro")}
            </p>

            <ul className="border-t border-border">
              {items.map((item) => (
                <li
                  key={item.title}
                  className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-6 border-b border-border py-3"
                >
                  <span className="sm:w-56 shrink-0 text-sm md:text-base font-bold tracking-tight text-foreground">
                    {item.title}
                  </span>
                  <span className="text-sm text-muted-foreground leading-relaxed">
                    {item.description}
                  </span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default memo(ShippingLogistics);
